/**
 * Setup Entity - Custom Form API Module
 * L_CUSTOM_FORM 관련 CRUD API
 */

/**
 * Question types.
 *
 * `group` is a composite question that contains child fields — used to express
 * a "main question" like "Directors" that groups labelled sub-fields (name,
 * ID type, nationality, DOB, …). When `repeatable === true`, the group can be
 * filled multiple times at render (Director #1, Director #2, …).
 */
export type CustomFormQuestionType =
  | 'short_text'
  | 'long_text'
  | 'single_choice'
  | 'multi_choice'
  | 'dropdown'
  | 'date'
  | 'number'
  | 'email'
  | 'file'
  | 'image'         // display-only inline image (uploaded by the builder admin)
  | 'single_check'  // checkbox-styled choices, but only one selectable (Yes/No etc.)
  | 'group';

/**
 * Supported common (base) code MAIN_CODEs that can back a choice/dropdown question.
 * B0X 계열은 공통(CM)에서, M0X 계열은 MY 관할 전용에서 로드된다.
 */
export type CustomFormBaseCode =
  | 'B03' | 'B04' | 'B05' | 'B08' | 'B15'
  | 'M01'  // Identification Type (MY)
  | 'M02'; // Race (MY)

export interface CustomFormQuestion {
  id: string;
  type: CustomFormQuestionType;
  label: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
  /**
   * When set on a choice-type question (single_choice / multi_choice / dropdown),
   * the options are resolved at render time from L_BASECODE by this MAIN_CODE
   * (via /api/basecode/auto/main/{sourceCode}), instead of using `options` above.
   */
  sourceCode?: CustomFormBaseCode | null;

  /**
   * Only for `type === 'group'`: labelled child questions rendered under this
   * group. Groups do not nest (child questions cannot themselves be groups).
   */
  fields?: CustomFormQuestion[];

  /**
   * Only for `type === 'group'`: whether the responder can add multiple
   * instances of this group (Director #1, #2, …).
   */
  repeatable?: boolean;

  /**
   * Only for `type === 'image'`: id of the uploaded image in
   * L_CUSTOM_FORM_IMAGES. The actual bytes are served from
   * `/api/setupEntity/customForm/image/{imageId}`.
   */
  imageId?: string | null;
  /** Optional caption shown under the image. */
  caption?: string;

  /**
   * Only for `type === 'date'`: the display/input format fixed for this field
   * (e.g. 'DD-MM-YYYY'). When set, the deep-link form uses it regardless of the
   * viewer's session date-format preference. Defaults to 'YYYY-MM-DD'.
   */
  dateFormat?: string;
}

/** Selectable date formats for a date question (mirrors the app's date-format picker). */
export const CUSTOM_FORM_DATE_FORMATS: string[] = [
  'YYYY-MM-DD',
  'MM/DD/YYYY',
  'DD/MM/YYYY',
  'YYYY/MM/DD',
  'DD-MM-YYYY',
  'YYYY.MM.DD',
  'MM.DD.YYYY',
  'DD.MM.YYYY'
];

export interface CustomFormSchema {
  questions: CustomFormQuestion[];
  /** Optional header logo — id of an uploaded image in L_CUSTOM_FORM_IMAGES. */
  logoImageId?: string | null;
}

export interface CustomFormListItem {
  no: number;
  formCd: string;
  clientCd: string;
  formTitle: string;
  formDesc: string;
  formStatus: string;
  requestDate: string | null;
  deepLinkCd: string | null;
  /** Number of submitted answers (from L_CUSTOM_FORM_ANSWER). */
  answerCount: number;
  /**
   * Mapping approval — 'DRAFT' (saved, review not requested yet), 'REQUESTED' (waiting for admin),
   * 'APPROVED', or null for forms created before this feature.
   */
  approvalStatus?: 'DRAFT' | 'REQUESTED' | 'APPROVED' | null;
  /** A created/edited version is waiting for admin approval (PENDING_FORM_SCHEMA_JSON). */
  hasPendingSchema?: boolean;
  /** An approved (live) version exists — deep link / responses use only this. */
  hasLiveSchema?: boolean;
  bigo: string;
  sysRegDate: string | null;
  sysRegId: string;
  sysRegName: string;
  sysModDate: string | null;
  sysModId: string;
  sysModName: string;
}

export interface CustomFormDetail extends CustomFormListItem {
  /** Builder edit target — pending version if any, otherwise the live one. */
  formSchema: CustomFormSchema;
  /** Approved (live) structure used by deep link, responses and preset autofill. null = new form not approved yet. */
  liveFormSchema?: CustomFormSchema | null;
  signerJson: unknown;
}

export interface CustomFormAnswerListItem {
  no: number;
  answerCd: string;
  formCd: string;
  clientCd: string;
  answerStatus: string;
  submitDate: string | null;
  deepLinkCd: string | null;
  fileCount: number;
  preview: string;
  tag: string;
  sysRegDate: string | null;
  sysRegId: string;
  sysRegName: string;
}

export interface CustomFormAnswerFile {
  fileCd: string;
  answerCd: string;
  formCd: string;
  questionId: string;
  parentGroupId: string | null;
  groupInstanceIdx: number | null;
  fileName: string;
  fileSize: number;
  contentType: string;
  sysRegDate: string | null;
}

export interface CustomFormAnswerDetail {
  answerCd: string;
  formCd: string;
  clientCd: string;
  answerStatus: string;
  submitDate: string | null;
  deepLinkCd: string | null;
  answerJson: { raw?: Record<string, any>; readable?: Record<string, any> } | null;
  tag: string;
  sysRegDate: string | null;
  sysRegId: string;
  sysRegName: string;
  files: CustomFormAnswerFile[];
}

export interface CustomFormCreatePayload {
  formTitle: string;
  formDesc?: string;
  formStatus?: string;
  formSchema?: CustomFormSchema;
  signerJson?: unknown;
  bigo?: string;
  /** true = submit for Lettaga review (REQUESTED + notify); otherwise saved as a draft. */
  requestReview?: boolean;
}

export interface CustomFormUpdatePayload {
  formTitle?: string;
  formDesc?: string;
  formStatus?: string;
  formSchema?: CustomFormSchema;
  signerJson?: unknown;
  bigo?: string;
  deepLinkCd?: string | null;
  /** true = submit for Lettaga review (REQUESTED + notify); otherwise saved as a draft. */
  requestReview?: boolean;
}

/** File payload for submission. `fileData` is base64 (no data-URI prefix). */
export interface CustomFormSubmitFile {
  questionId: string;
  parentGroupId?: string | null;
  groupInstanceIdx?: number | null;
  fileName: string;
  contentType?: string;
  fileSize?: number;
  fileData: string;
}

export interface CustomFormSubmitPayload {
  formCd: string;
  clientCd: string;
  answerStatus?: 'SUBMIT' | 'DRAFT';
  responses: Record<string, any>;
  deepLinkCd?: string | null;
  bigo?: string;
  files?: CustomFormSubmitFile[];
}

const BASE = '/api/setupEntity/customForm';

async function handle<T>(resp: Response): Promise<T> {
  const result = await resp.json().catch(() => ({}));
  if (!resp.ok || !result?.success) {
    const msg =
      (typeof result?.error === 'string' && result.error) ||
      result?.error?.message ||
      result?.message ||
      `Request failed (${resp.status})`;
    throw new Error(msg);
  }
  return result as T;
}

export const customFormApi = {
  async list(): Promise<{ success: true; items: CustomFormListItem[] }> {
    const resp = await fetch(`${BASE}/list`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    return handle(resp);
  },

  async detail(formCd: string): Promise<{ success: true; item: CustomFormDetail }> {
    const resp = await fetch(`${BASE}/detail/${encodeURIComponent(formCd)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    return handle(resp);
  },

  /** Public detail for the deep-link fill page (no auth required on server). */
  async publicDetail(
    formCd: string,
    clientCd: string
  ): Promise<{ success: true; item: Omit<CustomFormDetail, 'signerJson' | 'bigo' | 'sysRegDate' | 'sysRegId' | 'sysRegName' | 'sysModDate' | 'sysModId' | 'sysModName' | 'requestDate' | 'no'> }> {
    const params = new URLSearchParams({ formCd, clientCd });
    const resp = await fetch(`${BASE}/public-detail?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    return handle(resp);
  },

  async create(
    payload: CustomFormCreatePayload,
  ): Promise<{ success: true; formCd: string; approvalStatus?: 'DRAFT' | 'REQUESTED' }> {
    const resp = await fetch(`${BASE}/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    return handle(resp);
  },

  async update(
    formCd: string,
    payload: CustomFormUpdatePayload
  ): Promise<{
    success: true;
    formCd: string;
    schemaChanged?: boolean;
    hasPendingSchema?: boolean;
    approvalStatus?: 'DRAFT' | 'REQUESTED' | 'APPROVED' | null;
    reviewRequested?: boolean;
  }> {
    const resp = await fetch(`${BASE}/update/${encodeURIComponent(formCd)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    return handle(resp);
  },

  async submit(payload: CustomFormSubmitPayload): Promise<{ success: true; answerCd: string; fileCount: number }> {
    const resp = await fetch(`${BASE}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    return handle(resp);
  },

  async listAnswers(formCd: string): Promise<{ success: true; items: CustomFormAnswerListItem[]; count: number }> {
    const params = new URLSearchParams({ formCd });
    const resp = await fetch(`${BASE}/answers/list?${params.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    return handle(resp);
  },

  async downloadAnswerPdf(answerCd: string): Promise<Blob> {
    const resp = await fetch(`${BASE}/answers/detail/${encodeURIComponent(answerCd)}?format=pdf`, {
      credentials: 'include'
    });
    if (!resp.ok || !resp.headers.get('content-type')?.includes('application/pdf')) {
      throw new Error('PDF download failed. Please try again.');
    }
    return resp.blob();
  },

  async answerDetail(answerCd: string): Promise<{ success: true; item: CustomFormAnswerDetail }> {
    const resp = await fetch(`${BASE}/answers/detail/${encodeURIComponent(answerCd)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    return handle(resp);
  },

  /** Returns the URL to download a file (browser handles the actual download). */
  answerFileUrl(fileCd: string): string {
    return `${BASE}/answers/file/${encodeURIComponent(fileCd)}`;
  },

  /** URL to load an inline form image (public). */
  imageUrl(imageCd: string): string {
    return `${BASE}/image/${encodeURIComponent(imageCd)}`;
  },

  async uploadImage(payload: {
    formCd: string;
    fileName: string;
    contentType: string;
    fileSize: number;
    fileData: string; // base64
  }): Promise<{ success: true; imageCd: string }> {
    const resp = await fetch(`${BASE}/image`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    return handle(resp);
  },

  async updateAnswerTag(
    answerCd: string,
    tag: string
  ): Promise<{ success: true; answerCd: string; tag: string }> {
    const resp = await fetch(`${BASE}/answers/tag/${encodeURIComponent(answerCd)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ tag })
    });
    return handle(resp);
  },

  /** URL variants used by the file viewer / downloader. */
  answerFileInlineUrl(fileCd: string): string {
    return `${BASE}/answers/file/${encodeURIComponent(fileCd)}?inline=1`;
  },

  async updateAnswer(
    answerCd: string,
    payload: { responses: any; answerStatus?: string }
  ): Promise<{ success: true; answerCd: string }> {
    const resp = await fetch(`${BASE}/answers/update/${encodeURIComponent(answerCd)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload)
    });
    return handle(resp);
  },

  async remove(formCd: string): Promise<{ success: true; formCd: string }> {
    const resp = await fetch(`${BASE}/${encodeURIComponent(formCd)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include'
    });
    return handle(resp);
  }
};

export default customFormApi;
